import { Popover } from "@mui/material";
import {
  type FC,
  type MouseEvent,
  type ReactNode,
  useCallback,
  useMemo,
  useState,
} from "react";

import { FilterChip } from "~/components/UI/FilterChip";
import { FilterMenuTitle } from "~/components/UI/FilterMenuTitle";
import {
  DEFAULT_RANGE_SLIDER_MAX,
  DEFAULT_RANGE_SLIDER_MIN,
  DEFAULT_RANGE_SLIDER_STEP,
  isSameRangeValue,
} from "~/components/UI/FilterRangeSlider/utils";
import type { IRangeValue } from "~/types/range";
import { formatPercentageRange } from "~/utils/formatRange";

import { FilterRangeSliderForm } from "./components/FilterRangeSliderForm";

export interface FilterRangeSliderProps {
  label: string;
  icon?: ReactNode;
  locked?: boolean;
  onChange: (value: IRangeValue) => void;
  initialValue?: IRangeValue;
  min?: number;
  max?: number;
  step?: number;
  dialogTitle?: string;
  getHelpText?: (value: [number, number]) => string;
}

export const FilterRangeSlider: FC<FilterRangeSliderProps> = ({
  label,
  icon,
  locked,
  onChange,
  initialValue,
  min = DEFAULT_RANGE_SLIDER_MIN,
  max = DEFAULT_RANGE_SLIDER_MAX,
  step = DEFAULT_RANGE_SLIDER_STEP,
  dialogTitle,
  getHelpText,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const selectedLabel = useMemo(
    () => formatPercentageRange(initialValue),
    [initialValue]
  );

  const handleOpen = useCallback((event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleApply = useCallback(
    (value: IRangeValue) => {
      if (!isSameRangeValue(value, initialValue)) {
        onChange(value);
      }

      handleClose();
    },
    [handleClose, initialValue, onChange]
  );

  const handleClear = useCallback(() => onChange({}), [onChange]);

  return (
    <>
      <FilterChip
        label={label}
        icon={icon}
        valueLabel={selectedLabel || undefined}
        locked={locked}
        onClick={handleOpen}
        onClear={handleClear}
      />

      <Popover
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <FilterMenuTitle sx={{ px: 2, pt: 2 }}>{label}</FilterMenuTitle>
        <FilterRangeSliderForm
          initialValue={initialValue}
          min={min}
          max={max}
          step={step}
          onApply={handleApply}
          title={dialogTitle}
          getHelpText={getHelpText}
        />
      </Popover>
    </>
  );
};
